import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export const useSignedUrl = (bucket: string, path: string | null, expiresIn: number = 3600) => {
  const [signedUrl, setSignedUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!path) {
      setSignedUrl('');
      setLoading(false);
      return;
    }

    const getSignedUrl = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Check if path is already a full URL
        if (path.startsWith('http://') || path.startsWith('https://')) {
          // If it's a public URL, try to extract the file path and create a signed URL
          const match = path.match(/\/storage\/v1\/object\/public\/[^/]+\/(.+)$/);
          if (match) {
            const filePath = match[1];
            const { data, error: signedError } = await supabase.storage
              .from(bucket)
              .createSignedUrl(filePath, expiresIn);
            
            if (signedError) throw signedError;
            setSignedUrl(data?.signedUrl || path); // Fallback to original if signing fails
          } else {
            setSignedUrl(path); // Use as-is if we can't parse it
          }
        } else {
          // It's a file path, create signed URL
          const { data, error: signedError } = await supabase.storage
            .from(bucket)
            .createSignedUrl(path, expiresIn);
          
          if (signedError) throw signedError;
          setSignedUrl(data?.signedUrl || '');
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to get signed URL'));
        setSignedUrl(''); // Clear URL on error
      } finally {
        setLoading(false);
      }
    };

    getSignedUrl();
  }, [bucket, path, expiresIn]);

  return { signedUrl, loading, error };
};
